import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronRightIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { useAuthStore } from "../../../stores/auth";

interface Crumb {
  label: string;
  to?: string;
}

export function HelpLayout({
  crumbs,
  children,
}: {
  crumbs?: Crumb[];
  children: ReactNode;
}) {
  const location = useLocation();
  const isHub = location.pathname === "/help";
  const session = useAuthStore((s) => s.session);
  const isSignedIn = !!session;

  return (
    <div className="help-page min-h-[calc(100vh-5rem)] bg-cream-50">
      <div className="mx-auto max-w-6xl px-6 pt-10">
        {!isHub && (
          <nav aria-label="Breadcrumb" className="mb-6 text-sm text-bark-700">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/help" className="hover:text-forest-700">
                  Help centre
                </Link>
              </li>
              {crumbs?.map((crumb, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-bark-600/60" aria-hidden="true" />
                  {crumb.to ? (
                    <Link to={crumb.to} className="hover:text-forest-700">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-bark-900">{crumb.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}
      </div>

      <div className="mx-auto max-w-6xl px-6 pb-20">{children}</div>

      <div className="help-support border-t border-cream-300 bg-cream-100">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <div className="text-center">
            <h2 className="font-display text-2xl font-semibold text-bark-900">
              Still need help?
            </h2>
            <p className="mx-auto mt-2 max-w-2xl text-bark-700">
              {isSignedIn
                ? "Chat with our support team in the messenger at the bottom-right of the screen, or drop us an email — we reply within one working day."
                : "Sign in to chat with our support team in the messenger at the bottom-right of the screen, or drop us an email — we reply within one working day."}
            </p>
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {isSignedIn ? (
              <div className="paper-card flex h-full flex-col justify-between gap-4 rounded-2xl border border-forest-300 bg-cream-50 p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-forest-600 text-cream-50">
                    <ChatBubbleLeftRightIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-display text-lg font-semibold text-bark-900">
                      Live chat
                    </p>
                    <p className="mt-1 text-sm text-bark-700">
                      Click the messenger bubble in the bottom-right of the screen to start a conversation.
                    </p>
                  </div>
                </div>
                <p className="text-xs font-medium uppercase tracking-wider text-forest-700">
                  Signed in — ready to chat
                </p>
              </div>
            ) : (
              <Link
                to="/portal/login"
                className="paper-card group flex h-full flex-col justify-between gap-4 rounded-2xl border border-forest-300 bg-cream-50 p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-forest-600 text-cream-50">
                    <ChatBubbleLeftRightIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-display text-lg font-semibold text-bark-900 group-hover:text-forest-700">
                      Chat with support
                    </p>
                    <p className="mt-1 text-sm text-bark-700">
                      Sign in to your school portal, then use the messenger bubble at the bottom-right of the screen.
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-forest-700">
                  Sign in to the portal
                  <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </Link>
            )}

            <a
              href="mailto:support@bookburrow.uk"
              className="paper-card group flex h-full flex-col justify-between gap-4 rounded-2xl border border-cream-300 bg-cream-50 p-6 transition-all hover:-translate-y-0.5 hover:border-forest-300 hover:shadow-lg"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-cream-200 text-forest-700">
                  <EnvelopeIcon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div>
                  <p className="font-display text-lg font-semibold text-bark-900 group-hover:text-forest-700">
                    Email us
                  </p>
                  <p className="mt-1 text-sm text-bark-700">
                    Prefer email? Write to support@bookburrow.uk and we'll get back to you within one working day.
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-forest-700">
                support@bookburrow.uk
                <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
